import React, {createContext, Reducer, useContext} from "react";
import {
  AssignmentDetailInput,
  Driver,
  GeoPosition,
  GetRoutePlanningResultResponse,
  Job,
  ManifestStop,
  RoutePlanningMetricsInput
} from "../generated/graphql";
import {AddressType} from "../components/route-planning/types/types";
import {
  getDriverAddressText,
  getGeocodedAddress,
  getStopAddressText,
  getTimeZoneIdByLatLng,
  isValidAddress
} from "../utils/General";
import {Dayjs} from "dayjs";
import {combineDatesAndTime, findEarliestStartDate, isFutureDateTime, isStartLocationValid} from "../utils/DayjsUtils";
import {AsyncActionHandlers, useReducerAsync} from "use-reducer-async";
import {AddressValidationItem} from "../components/route-planning/validation-content/AddressValidationContent";
import {Constants, EmptyValueStrings} from "../constants/constants";
import {sortBy} from "lodash";
import {JobStopType} from "../utils/stops/StopUtils";
import {TUnsupportedJobDetails} from "../components/route-planning/validation-content/UnssportedJobTypesContent";
import {TMissingTimeWindowJobDetails} from "../components/route-planning/validation-content/TimeWindowValidationContent";
import {getManifestsWithLateStops, getStopTimeWindow} from "../components/route-planning/utils/RoutePlanning.utils";
import {OptimizationObjective} from "../components/settings/enums/RoutePlanning";

type RoutePlanningProviderProps = {
  children: JSX.Element[] | JSX.Element;
};

export type RoutePlanningDriver = Driver & {
  startLocation: string | null;
  startLocationType: AddressType;
  endLocation: string | null;
  endLocationType: AddressType | null;
  startDate: Dayjs | null;
  startTime: Dayjs | null;
  startTimeZone: string | null;
  vehicleDescription: string | null;
  isStartLocationValid: boolean;
  isStartDateTimeValid: boolean;
  isEndLocationValid: boolean;
  maxDrivingTime?: number;
  /** The manifest this driver entry is associated with. null = new driver. */
  manifestDriverId?: number | null;
};

export type AssignmentDetailWithEndDateTime = AssignmentDetailInput & {
  endDateTime?: string;
};

export type RoutePlanningState = {
  selectedDrivers: RoutePlanningDriver[];
  selectedJobs: Job[];
  allowMovingExistingWork: boolean;
  /** Manifest stops keyed by manifestDriverId — stored at Step 1 for the map screen. */
  manifestStopsMap: Record<number, ManifestStop[]>;
  dwellTime: number;
  optimizationObjective: OptimizationObjective;
  isSubmitted: boolean;
  submittedAt: number;
  currentStep: number;
  isLoading: boolean;
  originalResult: {
    result: GetRoutePlanningResultResponse;
    assignmentDetails: AssignmentDetailWithEndDateTime[];
    metrics: RoutePlanningMetricsInput;
  } | null;
};

export enum RoutePlanningActionType {
  GO_TO_NEXT_STEP = "GO_TO_NEXT_STEP",
  GO_TO_PREVIOUS_STEP = "GO_TO_PREVIOUS_STEP",
  GO_TO_STEP = "GO_TO_STEP",
  ADD_DRIVER = "ADD_DRIVER",
  REMOVE_DRIVER = "REMOVE_DRIVER",
  SET_SELECTED_DRIVERS = "SET_SELECTED_DRIVERS",
  SET_SELECTED_JOBS = "SET_SELECTED_JOBS",
  CLEAR_ALL_DRIVERS = "CLEAR_ALL_DRIVERS",
  UPDATE_START_LOCATION_FOR_DRIVER = "UPDATE_START_LOCATION_FOR_DRIVER",
  UPDATE_START_LOCATION_TYPE_FOR_DRIVER = "UPDATE_START_LOCATION_TYPE_FOR_DRIVER",
  UPDATE_START_LOCATION_FOR_ALL_DRIVERS = "UPDATE_START_LOCATION_FOR_ALL_DRIVERS",
  UPDATE_START_DATE_FOR_DRIVER = "UPDATE_START_DATE_FOR_DRIVER",
  UPDATE_START_TIME_FOR_DRIVER = "UPDATE_START_TIME_FOR_DRIVER",
  UPDATE_START_DATE_FOR_ALL_DRIVERS = "UPDATE_START_DATE_FOR_ALL_DRIVERS",
  UPDATE_START_DATE_TIME_FOR_ALL_DRIVERS = "UPDATE_START_DATE_TIME_FOR_ALL_DRIVERS",
  UPDATE_START_TIME_ZONE_FOR_DRIVER = "UPDATE_START_TIME_ZONE_FOR_DRIVER",
  UPDATE_START_TIME_ZONE_FOR_ALL_DRIVERS = "UPDATE_START_TIME_ZONE_FOR_ALL_DRIVERS",
  UPDATE_END_LOCATION_FOR_DRIVER = "UPDATE_END_LOCATION_FOR_DRIVER",
  UPDATE_END_LOCATION_TYPE_FOR_DRIVER = "UPDATE_END_LOCATION_TYPE_FOR_DRIVER",
  UPDATE_END_LOCATION_FOR_ALL_DRIVERS = "UPDATE_END_LOCATION_FOR_ALL_DRIVERS",
  UPDATE_DWELL_TIME = "UPDATE_DWELL_TIME",
  UPDATE_OPTIMIZATION_OBJECTIVE = "UPDATE_OPTIMIZATION_OBJECTIVE",
  UPDATE_IS_SUBMITTED = "UPDATE_IS_SUBMITTED",
  UPDATE_IS_LOADING = "UPDATE_IS_LOADING",
  UPDATE_STATE = "UPDATE_STATE",
  SET_ORIGINAL_RESULT = "SET_ORIGINAL_RESULT",
  RESET_CACHE = "RESET_CACHE",
  RESET_ALL = "RESET_ALL",
  ADD_MANIFEST = "ADD_MANIFEST",
  REMOVE_MANIFEST = "REMOVE_MANIFEST",
  SET_ALLOW_MOVING_WORK = "SET_ALLOW_MOVING_WORK"
}

export type RoutePlanningAction =
  | {type: RoutePlanningActionType.GO_TO_NEXT_STEP}
  | {type: RoutePlanningActionType.GO_TO_PREVIOUS_STEP}
  | {type: RoutePlanningActionType.GO_TO_STEP; payload: number}
  | {type: RoutePlanningActionType.ADD_DRIVER; payload: Driver & {vehicleDescription: string | null}}
  | {type: RoutePlanningActionType.REMOVE_DRIVER; payload: number}
  | {type: RoutePlanningActionType.SET_SELECTED_DRIVERS; payload: RoutePlanningDriver[]}
  | {type: RoutePlanningActionType.SET_SELECTED_JOBS; payload: Job[]}
  | {type: RoutePlanningActionType.CLEAR_ALL_DRIVERS}
  | {
      type: RoutePlanningActionType.UPDATE_START_LOCATION_FOR_DRIVER;
      payload: {
        driverId: number;
        manifestDriverId?: number;
        startLocation: string | null;
      };
    }
  | {
      type: RoutePlanningActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER;
      payload: {driverId: number; manifestDriverId?: number; startLocationType: AddressType};
    }
  | {
      type: RoutePlanningActionType.UPDATE_START_LOCATION_FOR_ALL_DRIVERS;
      payload: {startLocation: string; startLocationType: AddressType};
    }
  | {
      type: RoutePlanningActionType.UPDATE_START_DATE_FOR_DRIVER;
      payload: {driverId: number; manifestDriverId?: number; startDate: Dayjs | null};
    }
  | {
      type: RoutePlanningActionType.UPDATE_START_TIME_FOR_DRIVER;
      payload: {driverId: number; manifestDriverId?: number; startTime: Dayjs | null};
    }
  | {
      type: RoutePlanningActionType.UPDATE_START_DATE_FOR_ALL_DRIVERS;
      payload: Dayjs | null;
    }
  | {
      type: RoutePlanningActionType.UPDATE_START_DATE_TIME_FOR_ALL_DRIVERS;
      payload: {startDate: Dayjs | null; startTime: Dayjs | null; startTimeZone: string};
    }
  | {
      type: RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_DRIVER;
      payload: {driverId: number; manifestDriverId?: number; startTimeZone: string};
    }
  | {
      type: RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_ALL_DRIVERS;
      payload: {startTimeZone: string};
    }
  | {
      type: RoutePlanningActionType.UPDATE_END_LOCATION_FOR_DRIVER;
      payload: {
        driverId: number;
        manifestDriverId?: number;
        endLocation: string | null;
      };
    }
  | {
      type: RoutePlanningActionType.UPDATE_END_LOCATION_TYPE_FOR_DRIVER;
      payload: {driverId: number; manifestDriverId?: number; endLocationType: AddressType | null};
    }
  | {
      type: RoutePlanningActionType.UPDATE_END_LOCATION_FOR_ALL_DRIVERS;
      payload: {endLocation: string; endLocationType: AddressType | null};
    }
  | {
      type: RoutePlanningActionType.UPDATE_DWELL_TIME;
      payload: number;
    }
  | {type: RoutePlanningActionType.UPDATE_OPTIMIZATION_OBJECTIVE; payload: OptimizationObjective}
  | {type: RoutePlanningActionType.UPDATE_IS_SUBMITTED; payload: boolean}
  | {type: RoutePlanningActionType.UPDATE_IS_LOADING; payload: boolean}
  | {type: RoutePlanningActionType.UPDATE_STATE; payload: Partial<RoutePlanningState>}
  | {
      type: RoutePlanningActionType.SET_ORIGINAL_RESULT;
      payload: {
        result: GetRoutePlanningResultResponse;
        assignmentDetails: AssignmentDetailWithEndDateTime[];
        metrics: RoutePlanningMetricsInput;
      };
    }
  | {type: RoutePlanningActionType.RESET_CACHE}
  | {type: RoutePlanningActionType.RESET_ALL; payload: {dwellTime: number}}
  | {
      type: RoutePlanningActionType.ADD_MANIFEST;
      payload: {
        manifestDriverId: number;
        driverId: number;
        driverName: string;
        driverCode: string;
        vehicleDescription?: string | null;
        skills?: string;
        stops?: ManifestStop[];
      };
    }
  | {type: RoutePlanningActionType.REMOVE_MANIFEST; payload: number}
  | {type: RoutePlanningActionType.SET_ALLOW_MOVING_WORK; payload: boolean};

export enum RoutePlanningAsyncActionType {
  GO_TO_NEXT_STEP_ASYNC = "GO_TO_NEXT_STEP_ASYNC",
  UPDATE_START_LOCATION_FOR_DRIVER_ASYNC = "UPDATE_START_LOCATION_FOR_DRIVER_ASYNC",
  UPDATE_START_LOCATION_FOR_ALL_DRIVERS_ASYNC = "UPDATE_START_LOCATION_FOR_ALL_DRIVERS_ASYNC",
  UPDATE_START_LOCATION_TYPE_FOR_DRIVER_ASYNC = "UPDATE_START_LOCATION_TYPE_FOR_DRIVER_ASYNC",
  UPDATE_END_LOCATION_FOR_DRIVER_ASYNC = "UPDATE_END_LOCATION_FOR_DRIVER_ASYNC",
  UPDATE_END_LOCATION_FOR_ALL_DRIVERS_ASYNC = "UPDATE_END_LOCATION_FOR_ALL_DRIVERS_ASYNC",
  UPDATE_END_LOCATION_TYPE_FOR_DRIVER_ASYNC = "UPDATE_END_LOCATION_TYPE_FOR_DRIVER_ASYNC"
}

export enum RoutePlanningErrorType {
  OPTIMIZATION_OBJECTIVE_REQUIRED = "OPTIMIZATION_OBJECTIVE_REQUIRED",
  SELECTION_REQUIRED = "SELECTION_REQUIRED",
  STOPS_ADDRESSES_INVALID = "STOPS_ADDRESSES_INVALID",
  DRIVER_INFORMATION_INVALID = "DRIVER_INFORMATION_INVALID",
  DRIVER_START_END_LOCATION_INVALID = "DRIVER_START_END_LOCATION_INVALID",
  UNSUPPORTED_JOBS = "UNSUPPORTED_JOBS",
  MISSING_TIME_WINDOW = "MISSING_TIME_WINDOW",
  CAN_NOT_ASSIGN_JOBS = "CAN_NOT_ASSIGN_JOBS",
  MANIFEST_JOBS_UNASSIGNED = "MANIFEST_JOBS_UNASSIGNED",
  INVALID_DATA = "INVALID_DATA",
  MANIFEST_STOPS_PROJECTED_LATE = "MANIFEST_STOPS_PROJECTED_LATE"
}

// Need async actions to handle side effects like fetching time zones
export type RoutePlanningAsyncAction =
  | {
      type: RoutePlanningAsyncActionType.GO_TO_NEXT_STEP_ASYNC;
      payload: {
        successCallback?: () => Promise<boolean>;
        errorCallback?: (type: RoutePlanningErrorType, content?: any) => any;
        allowBothStops?: boolean;
        existingStopsEnabled?: boolean;
      };
    }
  | {
      type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_FOR_DRIVER_ASYNC;
      payload: {driverId: number; manifestDriverId?: number; startLocation: string | null};
    }
  | {
      type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_FOR_ALL_DRIVERS_ASYNC;
      payload: {startLocation: string; startLocationType: AddressType};
    }
  | {
      type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER_ASYNC;
      payload: {driverId: number; manifestDriverId?: number; startLocationType: AddressType; startLocation?: string};
    }
  | {
      type: RoutePlanningAsyncActionType.UPDATE_END_LOCATION_FOR_DRIVER_ASYNC;
      payload: {driverId: number; manifestDriverId?: number; endLocation: string | null};
    }
  | {
      type: RoutePlanningAsyncActionType.UPDATE_END_LOCATION_FOR_ALL_DRIVERS_ASYNC;
      payload: {endLocation: string; endLocationType: AddressType | null};
    }
  | {
      type: RoutePlanningAsyncActionType.UPDATE_END_LOCATION_TYPE_FOR_DRIVER_ASYNC;
      payload: {driverId: number; manifestDriverId?: number; endLocationType: AddressType | null; endLocation?: string};
    };

// Clear-on-overflow: when a cache exceeds this limit the entire map is cleared
// before adding the new entry. An LRU approach would be more efficient but adds
// complexity; 500 entries covers typical session usage so full-clears are rare.
const MAX_CACHE_ENTRIES = 500;

export const TimezoneCache = new Map<string, string>();
export const GeocodedAddressesMap = new Map<string, GeoPosition>();
export const ValidatedAddressesMap = new Map<string, boolean>();

export const getTimeZoneWithCache = async (startLocation: string | null) => {
  if (!startLocation) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  if (TimezoneCache.has(startLocation)) {
    return TimezoneCache.get(startLocation) as string;
  }

  const position = await getAddressPositionWithCache(startLocation);
  if (!position) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  const timeZone = await getTimeZoneIdByLatLng(position.latitude, position.longitude);
  if (!timeZone) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  if (TimezoneCache.size >= MAX_CACHE_ENTRIES) TimezoneCache.clear();
  TimezoneCache.set(startLocation, timeZone);
  return timeZone;
};

export const getAddressPositionWithCache = async (address: string): Promise<GeoPosition | undefined> => {
  if (GeocodedAddressesMap.has(address)) {
    return GeocodedAddressesMap.get(address);
  }
  const position = await getGeocodedAddress(address);
  if (position) {
    if (GeocodedAddressesMap.size >= MAX_CACHE_ENTRIES) GeocodedAddressesMap.clear();
    GeocodedAddressesMap.set(address, position);
  }
  return position;
};

export const isValidAddressWithCache = async (address: string): Promise<boolean> => {
  if (ValidatedAddressesMap.has(address)) {
    return ValidatedAddressesMap.get(address) as boolean;
  }
  // Run validation and geocode position in parallel. getGeocodedAddress
  // populates GeocodedAddressesMap so that getAddressPositionWithCache
  // (e.g. warehouse detection) can reuse the cached position later.
  const [isValid] = await Promise.all([isValidAddress(address), getAddressPositionWithCache(address)]);
  if (ValidatedAddressesMap.size >= MAX_CACHE_ENTRIES) ValidatedAddressesMap.clear();
  ValidatedAddressesMap.set(address, isValid);
  return isValid;
};

export const validateDriversRequiredInfo = (state: RoutePlanningState) => {
  const validatedDrivers = state.selectedDrivers.map((driver) => {
    return {
      ...driver,
      isStartLocationValid: isStartLocationValid(driver.startLocation),
      isStartDateTimeValid: isFutureDateTime(
        combineDatesAndTime(driver.startDate, driver.startTime, driver.startTimeZone)
      ),
      isEndLocationValid: driver.endLocationType === null || isStartLocationValid(driver.endLocation)
    };
  });
  return validatedDrivers;
};

const InitialState: RoutePlanningState = {
  selectedDrivers: [],
  selectedJobs: [],
  allowMovingExistingWork: false,
  manifestStopsMap: {},
  dwellTime: 0,
  optimizationObjective: OptimizationObjective.MinCompletionTime,
  isSubmitted: false,
  submittedAt: 0,
  currentStep: 1,
  isLoading: false,
  originalResult: null
};

/** Match a driver entry by manifestDriverId (for manifest rows) or driverId (for new drivers). */
const matchesDriverKey = (driver: RoutePlanningDriver, driverId: number, manifestDriverId?: number): boolean => {
  if (manifestDriverId != null) {
    return driver.manifestDriverId === manifestDriverId;
  }
  return driver.driverId === driverId && !driver.manifestDriverId;
};

const findDriverByKey = (
  drivers: RoutePlanningDriver[],
  driverId: number,
  manifestDriverId?: number
): RoutePlanningDriver | undefined => {
  if (manifestDriverId != null) {
    return drivers.find((d) => d.manifestDriverId === manifestDriverId);
  }
  return drivers.find((d) => d.driverId === driverId && !d.manifestDriverId);
};

const reducer = (state: RoutePlanningState, action: RoutePlanningAction): RoutePlanningState => {
  let newState: RoutePlanningState;
  switch (action.type) {
    case RoutePlanningActionType.GO_TO_NEXT_STEP:
      newState = {
        ...state,
        currentStep: state.currentStep + 1
      };
      break;

    case RoutePlanningActionType.GO_TO_PREVIOUS_STEP:
      newState = {
        ...state,
        currentStep: state.currentStep - 1,
        isSubmitted: false
      };
      break;

    case RoutePlanningActionType.GO_TO_STEP:
      newState = {
        ...state,
        currentStep: action.payload
      };
      break;

    case RoutePlanningActionType.ADD_DRIVER: {
      const addDriver: RoutePlanningDriver = {
        ...action.payload,
        startLocation: getDriverAddressText(action.payload),
        startLocationType: AddressType.HOME,
        endLocation: null,
        endLocationType: null,
        startDate: null,
        startTime: null,
        startTimeZone: null,
        isStartDateTimeValid: false,
        isStartLocationValid: false,
        isEndLocationValid: true
      };

      newState = {
        ...state,
        selectedDrivers: [...state.selectedDrivers, addDriver]
      };
      break;
    }

    case RoutePlanningActionType.REMOVE_DRIVER:
      // Only remove new drivers (not manifest entries)
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.filter(
          (driver) => driver.driverId !== action.payload || !!driver.manifestDriverId
        )
      };
      break;

    case RoutePlanningActionType.SET_SELECTED_DRIVERS:
      // Preserve manifest entries, replace only new drivers
      newState = {
        ...state,
        selectedDrivers: [...state.selectedDrivers.filter((d) => !!d.manifestDriverId), ...action.payload]
      };
      break;

    case RoutePlanningActionType.SET_SELECTED_JOBS:
      newState = {
        ...state,
        selectedJobs: action.payload
      };
      break;

    case RoutePlanningActionType.CLEAR_ALL_DRIVERS:
      // Only clear new drivers, keep manifest entries
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.filter((d) => !!d.manifestDriverId)
      };
      break;

    case RoutePlanningActionType.UPDATE_START_LOCATION_FOR_DRIVER: {
      const {driverId, manifestDriverId, startLocation} = action.payload;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          if (matchesDriverKey(driver, driverId, manifestDriverId)) {
            const updatedDriver = {
              ...driver,
              startLocation: startLocation
            };
            if (driver.endLocationType === AddressType.START_LOCATION) {
              updatedDriver.endLocation = startLocation;
            }
            return updatedDriver;
          }
          return driver;
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER: {
      const {driverId, manifestDriverId, startLocationType} = action.payload;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          if (matchesDriverKey(driver, driverId, manifestDriverId)) {
            return {
              ...driver,
              startLocationType: startLocationType
            };
          }
          return driver;
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_START_LOCATION_FOR_ALL_DRIVERS: {
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          const startLocation =
            action.payload.startLocationType === AddressType.HOME
              ? getDriverAddressText(driver)
              : action.payload.startLocation;
          const updatedDriver = {
            ...driver,
            startLocation: startLocation,
            startLocationType: action.payload.startLocationType
          };
          if (driver.endLocationType === AddressType.START_LOCATION) {
            updatedDriver.endLocation = startLocation;
          }
          return updatedDriver;
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_END_LOCATION_FOR_DRIVER: {
      const {driverId, manifestDriverId, endLocation} = action.payload;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          if (matchesDriverKey(driver, driverId, manifestDriverId)) {
            return {
              ...driver,
              endLocation: endLocation
            };
          }
          return driver;
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_END_LOCATION_TYPE_FOR_DRIVER: {
      const {driverId, manifestDriverId, endLocationType} = action.payload;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          if (matchesDriverKey(driver, driverId, manifestDriverId)) {
            return {
              ...driver,
              endLocationType: endLocationType
            };
          }
          return driver;
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_END_LOCATION_FOR_ALL_DRIVERS: {
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          let endLocation: string | null = null;
          if (action.payload.endLocationType === AddressType.HOME) {
            endLocation = getDriverAddressText(driver);
          } else if (action.payload.endLocationType === AddressType.START_LOCATION) {
            endLocation = driver.startLocation;
          } else if (action.payload.endLocationType === null) {
            endLocation = null;
          } else {
            endLocation = action.payload.endLocation;
          }
          return {
            ...driver,
            endLocation: endLocation,
            endLocationType: action.payload.endLocationType
          };
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_START_DATE_FOR_DRIVER: {
      const {driverId, manifestDriverId, startDate} = action.payload;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          if (matchesDriverKey(driver, driverId, manifestDriverId)) {
            return {
              ...driver,
              startDate: startDate
            };
          }
          return driver;
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_START_TIME_FOR_DRIVER: {
      const {driverId, manifestDriverId, startTime} = action.payload;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          if (matchesDriverKey(driver, driverId, manifestDriverId)) {
            return {
              ...driver,
              startTime: startTime?.tz(driver.startTimeZone!, true) || null
            };
          }
          return driver;
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_START_DATE_FOR_ALL_DRIVERS: {
      const startDate = action.payload;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          return {
            ...driver,
            startDate: startDate ? startDate.tz(driver.startTimeZone!, true) : null
          };
        })
      };

      break;
    }

    case RoutePlanningActionType.UPDATE_START_DATE_TIME_FOR_ALL_DRIVERS: {
      const {startDate, startTime, startTimeZone} = action.payload;
      if (startDate?.isValid() && startTime?.isValid()) {
        const combinedDateTime = combineDatesAndTime(startDate, startTime, startTimeZone);
        newState = {
          ...state,
          selectedDrivers: state.selectedDrivers.map((driver) => {
            return {
              ...driver,
              startDate: combinedDateTime,
              startTime: combinedDateTime
            };
          })
        };
      } else {
        newState = {
          ...state,
          selectedDrivers: state.selectedDrivers.map((driver) => {
            return {
              ...driver,
              startDate: startDate?.isValid() ? startDate.tz(driver.startTimeZone!, true) : null,
              startTime: startTime?.isValid() ? startTime.tz(driver.startTimeZone!) : null
            };
          })
        };
      }
      break;
    }

    case RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_DRIVER: {
      const {driverId, manifestDriverId, startTimeZone} = action.payload;
      const driver = findDriverByKey(state.selectedDrivers, driverId, manifestDriverId);
      if (!driver) {
        newState = {...state};
        break;
      }

      const {startDate, startTime} = driver;
      let newStartDate = startDate;
      let newStartTime = startTime;
      if (startDate && !startTime) {
        newStartDate = startDate.tz(startTimeZone);
      } else if (!startDate && startTime?.isValid()) {
        newStartTime = startTime.tz(startTimeZone);
      } else if (startDate?.isValid() && startTime?.isValid()) {
        const combinedDateTime = combineDatesAndTime(startDate, startTime, startTimeZone);
        newStartDate = combinedDateTime;
        newStartTime = combinedDateTime;
      }
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          if (matchesDriverKey(driver, driverId, manifestDriverId)) {
            return {
              ...driver,
              startDate: newStartDate,
              startTime: newStartTime,
              startTimeZone: startTimeZone
            };
          }
          return driver;
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_ALL_DRIVERS: {
      const startTimeZone = action.payload.startTimeZone;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.map((driver) => {
          const {startDate, startTime} = driver;
          let newStartDate = startDate;
          let newStartTime = startTime;
          if (startDate && !startTime) {
            newStartDate = startDate.tz(startTimeZone);
          } else if (!startDate && startTime?.isValid()) {
            newStartTime = startTime.tz(startTimeZone);
          } else if (startDate?.isValid() && startTime?.isValid()) {
            const combinedDateTime = combineDatesAndTime(startDate, startTime, startTimeZone);
            newStartDate = combinedDateTime;
            newStartTime = combinedDateTime;
          }
          return {
            ...driver,
            startDate: newStartDate,
            startTime: newStartTime,
            startTimeZone: startTimeZone
          };
        })
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_DWELL_TIME: {
      newState = {
        ...state,
        dwellTime: action.payload
      };
      break;
    }

    case RoutePlanningActionType.UPDATE_OPTIMIZATION_OBJECTIVE:
      newState = {
        ...state,
        optimizationObjective: action.payload
      };
      break;

    case RoutePlanningActionType.UPDATE_IS_SUBMITTED:
      newState = {
        ...state,
        isSubmitted: action.payload,
        submittedAt: action.payload ? Date.now() : state.submittedAt
      };
      break;

    case RoutePlanningActionType.UPDATE_IS_LOADING:
      newState = {
        ...state,
        isLoading: action.payload
      };
      break;

    case RoutePlanningActionType.UPDATE_STATE:
      newState = {
        ...state,
        ...action.payload
      };
      break;

    case RoutePlanningActionType.SET_ORIGINAL_RESULT:
      newState = {
        ...state,
        originalResult: action.payload
      };
      break;

    case RoutePlanningActionType.ADD_MANIFEST: {
      const {manifestDriverId, driverId, driverName, driverCode, vehicleDescription, skills, stops} = action.payload;
      const alreadyExists = state.selectedDrivers.some((d) => d.manifestDriverId === manifestDriverId);
      if (alreadyExists) {
        newState = {...state};
      } else {
        const newEntry: RoutePlanningDriver = {
          driverId,
          driverCode,
          name: driverName,
          skills: skills ?? "",
          manifestDriverId,
          startLocation: null,
          startLocationType: AddressType.HOME,
          endLocation: null,
          endLocationType: null,
          startDate: null,
          startTime: null,
          startTimeZone: null,
          vehicleDescription: vehicleDescription ?? null,
          isStartLocationValid: false,
          isStartDateTimeValid: false,
          isEndLocationValid: true
        } as RoutePlanningDriver;
        newState = {
          ...state,
          selectedDrivers: [...state.selectedDrivers, newEntry],
          manifestStopsMap: stops ? {...state.manifestStopsMap, [manifestDriverId]: stops} : state.manifestStopsMap
        };
      }
      break;
    }

    case RoutePlanningActionType.REMOVE_MANIFEST: {
      const manifestDriverId = action.payload;
      const {[manifestDriverId]: _, ...remainingStops} = state.manifestStopsMap;
      newState = {
        ...state,
        selectedDrivers: state.selectedDrivers.filter((d) => d.manifestDriverId !== manifestDriverId),
        manifestStopsMap: remainingStops
      };
      break;
    }

    case RoutePlanningActionType.SET_ALLOW_MOVING_WORK:
      newState = {
        ...state,
        allowMovingExistingWork: action.payload
      };
      break;

    case RoutePlanningActionType.RESET_CACHE:
      TimezoneCache.clear();
      GeocodedAddressesMap.clear();
      ValidatedAddressesMap.clear();
      newState = {...state};
      break;

    case RoutePlanningActionType.RESET_ALL:
      newState = {
        ...InitialState,
        dwellTime: action.payload.dwellTime,
        currentStep: state.currentStep,
        optimizationObjective: state.optimizationObjective
      };
      break;

    default:
      newState = {...state};
  }

  return newState.isSubmitted ? {...newState, selectedDrivers: validateDriversRequiredInfo(newState)} : newState;
};

const asyncActions: AsyncActionHandlers<Reducer<RoutePlanningState, RoutePlanningAction>, RoutePlanningAsyncAction> = {
  [RoutePlanningAsyncActionType.GO_TO_NEXT_STEP_ASYNC]:
    ({dispatch, getState}) =>
    async (action) => {
      const {successCallback, errorCallback, allowBothStops, existingStopsEnabled} = action.payload;
      const currentStep = getState().currentStep;

      dispatch({type: RoutePlanningActionType.UPDATE_IS_LOADING, payload: true});
      switch (currentStep) {
        case 1: {
          //Validate optimization objective
          const optimizationObjective = getState().optimizationObjective;
          if (optimizationObjective === OptimizationObjective.Both) {
            if (errorCallback) {
              errorCallback(RoutePlanningErrorType.OPTIMIZATION_OBJECTIVE_REQUIRED);
            }
            break;
          }

          //Validate selection
          const jobsCount = getState().selectedJobs.length;
          const allDrivers = getState().selectedDrivers;
          const driversCount = allDrivers.filter((d) => !d.manifestDriverId).length;
          const manifestsCount = allDrivers.filter((d) => !!d.manifestDriverId).length;
          const totalDriverEntities = driversCount + manifestsCount;
          const driverLabel = existingStopsEnabled ? "drivers or manifests" : "drivers";
          if (jobsCount < 2 || totalDriverEntities < 2) {
            let message = "";
            if (jobsCount < 2 && totalDriverEntities < 2) {
              message = `At least 2 jobs and 2 ${driverLabel} must be selected in order to continue`;
            } else if (jobsCount < 2) {
              message = "At least 2 jobs must be selected in order to continue";
            } else if (totalDriverEntities < 2) {
              message = `At least 2 ${driverLabel} must be selected in order to continue`;
            } else if (totalDriverEntities > Constants.OPTIMIZATION.MAX_DRIVERS) {
              message = `A maximum of ${Constants.OPTIMIZATION.MAX_DRIVERS} ${driverLabel} can be selected for route planning`;
            } else {
              const totalStopsCount = getState().selectedJobs.reduce((sum, job) => sum + job.stops.length, 0);
              if (totalStopsCount > Constants.OPTIMIZATION.MAX_STOPS) {
                message = `The selected jobs contain ${totalStopsCount} stops, which exceeds the maximum of ${Constants.OPTIMIZATION.MAX_STOPS} stops for route planning`;
              }
            }

            if (errorCallback && message) {
              errorCallback(RoutePlanningErrorType.SELECTION_REQUIRED, message);
              break;
            }
          }

          //validate jobs should not contains Both stop type and not have multiple(>= 2) pickups and multiple deliveries
          const multiplePickupsAndDeliveriesJobs: TUnsupportedJobDetails[] = [];
          const bothStopJobs: TUnsupportedJobDetails[] = [];

          for (const job of getState().selectedJobs) {
            const pickupStops = job.stops.filter((stop) => stop.stopType === JobStopType.PICKUP);
            const deliveryStops = job.stops.filter((stop) => stop.stopType === JobStopType.DELIVERY);
            if (pickupStops.length >= 2 && deliveryStops.length >= 2) {
              multiplePickupsAndDeliveriesJobs.push({
                jobNumber: job.jobNumber,
                jobId: job.jobId,
                orderId: job.order.orderId
              });
            }
            if (!allowBothStops) {
              const hasBothStop = job.stops.some((stop) => stop.stopType === JobStopType.BOTH);
              if (hasBothStop) {
                bothStopJobs.push({
                  jobNumber: job.jobNumber,
                  jobId: job.jobId,
                  orderId: job.order.orderId
                });
              }
            }
          }

          if (multiplePickupsAndDeliveriesJobs.length > 0 || bothStopJobs.length > 0) {
            if (errorCallback) {
              errorCallback(RoutePlanningErrorType.UNSUPPORTED_JOBS, {
                multiplePickupsAndDeliveriesJobs,
                bothStopJobs
              });
              break;
            }
          }

          // Validate time windows
          const missingTimeWindowJobs: TMissingTimeWindowJobDetails[] = [];
          for (const job of getState().selectedJobs) {
            const isContainsMissingTimeWindowStops = job.stops.some((stop) => {
              const timeWindow = getStopTimeWindow(stop, job);
              return stop.stopType === JobStopType.PICKUP && timeWindow[1] === null;
            });
            if (isContainsMissingTimeWindowStops) {
              missingTimeWindowJobs.push({
                jobNumber: job.jobNumber,
                jobId: job.jobId,
                orderId: job.order.orderId
              });
            }
          }
          if (missingTimeWindowJobs.length > 0) {
            if (errorCallback) {
              errorCallback(RoutePlanningErrorType.MISSING_TIME_WINDOW, missingTimeWindowJobs);
              break;
            }
          }

          // Validate stop addresses
          const invalidAddressStops: AddressValidationItem[] = [];
          const addressValidationPromises: Promise<void>[] = [];
          for (const job of getState().selectedJobs) {
            for (const stop of job.stops) {
              const stopAdress = getStopAddressText({stop, hasZip: true});
              if (stopAdress) {
                addressValidationPromises.push(
                  isValidAddressWithCache(stopAdress).then((isValid) => {
                    if (!isValid) {
                      invalidAddressStops.push({
                        identifier: `Stop #${stop.sequence}`,
                        address: stopAdress,
                        groupId: `Job #${job.jobNumber}`,
                        orderId: job.order.orderId,
                        jobId: job.jobId
                      });
                    }
                  })
                );
              } else {
                invalidAddressStops.push({
                  identifier: `Stop #${stop.sequence}`,
                  address: EmptyValueStrings.notProvided,
                  groupId: `Job #${job.jobNumber}`,
                  orderId: job.order.orderId,
                  jobId: job.jobId
                });
              }
            }
          }
          await Promise.all(addressValidationPromises);
          if (invalidAddressStops.length > 0) {
            if (errorCallback) {
              errorCallback(
                RoutePlanningErrorType.STOPS_ADDRESSES_INVALID,
                sortBy(invalidAddressStops, ["groupId", "identifier"])
              );
              break;
            }
          }

          const driversWithoutTimeZone = getState().selectedDrivers.filter((driver) => !driver.startTimeZone);
          if (driversWithoutTimeZone.length > 0) {
            const driverStartAddresses = Array.from(new Set(driversWithoutTimeZone.map(getDriverAddressText)));
            const timezones = await Promise.all(driverStartAddresses.map(getTimeZoneWithCache));
            const timezoneMap = new Map<string, string>();
            driverStartAddresses.forEach((address, idx) => {
              timezoneMap.set(address, timezones[idx]);
            });

            driversWithoutTimeZone.forEach((driver) => {
              const timezone = timezoneMap.get(getDriverAddressText(driver));
              if (timezone) {
                dispatch({
                  type: RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_DRIVER,
                  payload: {
                    driverId: driver.driverId!,
                    manifestDriverId: driver.manifestDriverId ?? undefined,
                    startTimeZone: timezone
                  }
                });
              }
            });
          }

          const earliestJobStopDateTime = findEarliestStartDate(getState().selectedJobs.flatMap((job) => job.stops));
          getState().selectedDrivers.forEach((driver) => {
            if (driver.startDate?.isValid() && driver.startTime) return;
            dispatch({
              type: RoutePlanningActionType.UPDATE_START_DATE_FOR_DRIVER,
              payload: {
                driverId: driver.driverId!,
                manifestDriverId: driver.manifestDriverId ?? undefined,
                startDate: earliestJobStopDateTime
              }
            });
          });
          dispatch({type: RoutePlanningActionType.GO_TO_NEXT_STEP});
          break;
        }

        case 2: {
          dispatch({type: RoutePlanningActionType.UPDATE_IS_SUBMITTED, payload: true});
          const validatedDrivers = validateDriversRequiredInfo(getState());
          dispatch({type: RoutePlanningActionType.UPDATE_STATE, payload: {selectedDrivers: validatedDrivers}});
          const invalidDriver = validatedDrivers.find(
            (driver) => !driver.isStartLocationValid || !driver.isStartDateTimeValid || !driver.isEndLocationValid
          );
          let isDriversInformationInvalid = false;
          if (invalidDriver) {
            isDriversInformationInvalid = true;
            if (errorCallback) {
              errorCallback(RoutePlanningErrorType.DRIVER_INFORMATION_INVALID);
            }
          } else {
            // Validate driver start and end locations
            const invalidDriverStartEndLocations: AddressValidationItem[] = [];
            const addressValidationPromises: Promise<void>[] = [];
            for (const driver of validatedDrivers) {
              const driverStartLocation = driver.startLocation;
              const driverEndLocation = driver.endLocation;

              const startPromise = driverStartLocation
                ? isValidAddressWithCache(driverStartLocation).then((isValid) => (isValid ? null : driverStartLocation))
                : Promise.resolve(EmptyValueStrings.notProvided);

              let endPromise: Promise<string | null>;
              if (driver.endLocationType === null) {
                endPromise = Promise.resolve(null);
              } else if (driverEndLocation) {
                endPromise = isValidAddressWithCache(driverEndLocation).then((isValid) =>
                  isValid ? null : driverEndLocation
                );
              } else {
                endPromise = Promise.resolve(EmptyValueStrings.notProvided);
              }

              addressValidationPromises.push(
                Promise.all([startPromise, endPromise]).then(([invalidStart, invalidEnd]) => {
                  if (invalidStart) {
                    invalidDriverStartEndLocations.push({
                      groupId: `Driver #${driver.driverCode}`,
                      identifier: "Start Location",
                      address: invalidStart
                    });
                  }
                  if (invalidEnd) {
                    invalidDriverStartEndLocations.push({
                      groupId: `Driver #${driver.driverCode}`,
                      identifier: "End Location",
                      address: invalidEnd
                    });
                  }
                })
              );
            }

            await Promise.all(addressValidationPromises);

            if (invalidDriverStartEndLocations.length > 0) {
              isDriversInformationInvalid = true;
              if (errorCallback) {
                errorCallback(
                  RoutePlanningErrorType.DRIVER_START_END_LOCATION_INVALID,
                  sortBy(invalidDriverStartEndLocations, ["groupId"])
                );
              }
            }
          }
          // DP-3202: Pre-check — flag manifests with stops already past their late time
          if (!isDriversInformationInvalid && Object.keys(getState().manifestStopsMap).length > 0) {
            const lateManifests = getManifestsWithLateStops(
              getState().manifestStopsMap,
              validatedDrivers,
              getState().allowMovingExistingWork
            );
            if (lateManifests.length > 0) {
              isDriversInformationInvalid = true;
              if (errorCallback) {
                errorCallback(RoutePlanningErrorType.MANIFEST_STOPS_PROJECTED_LATE, lateManifests);
              }
            }
          }

          if (isDriversInformationInvalid) {
            break;
          } else if (successCallback) {
            const isSuccess = await successCallback();
            if (isSuccess) {
              dispatch({
                type: RoutePlanningActionType.GO_TO_NEXT_STEP
              });
            }
          }
          break;
        }
        default:
          break;
      }

      dispatch({type: RoutePlanningActionType.UPDATE_IS_LOADING, payload: false});
    },
  [RoutePlanningAsyncActionType.UPDATE_START_LOCATION_FOR_DRIVER_ASYNC]:
    ({dispatch}) =>
    async (action) => {
      const {driverId, manifestDriverId, startLocation} = action.payload;
      dispatch({
        type: RoutePlanningActionType.UPDATE_START_LOCATION_FOR_DRIVER,
        payload: action.payload
      });
      const timezone = await getTimeZoneWithCache(startLocation);
      dispatch({
        type: RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_DRIVER,
        payload: {driverId, manifestDriverId, startTimeZone: timezone}
      });
    },
  [RoutePlanningAsyncActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER_ASYNC]:
    ({dispatch, getState}) =>
    async (action) => {
      const {driverId, manifestDriverId, startLocationType} = action.payload;
      dispatch({
        type: RoutePlanningActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER,
        payload: action.payload
      });
      const targetDriver = findDriverByKey(getState().selectedDrivers, driverId, manifestDriverId);
      const startLocation =
        startLocationType === AddressType.HOME
          ? getDriverAddressText(targetDriver!)
          : action.payload.startLocation ?? null;
      dispatch({
        type: RoutePlanningActionType.UPDATE_START_LOCATION_FOR_DRIVER,
        payload: {driverId, manifestDriverId, startLocation}
      });
      const timezone = await getTimeZoneWithCache(startLocation);
      dispatch({
        type: RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_DRIVER,
        payload: {driverId, manifestDriverId, startTimeZone: timezone}
      });
    },
  [RoutePlanningAsyncActionType.UPDATE_START_LOCATION_FOR_ALL_DRIVERS_ASYNC]:
    ({dispatch, getState}) =>
    async (action) => {
      const {startLocation, startLocationType} = action.payload;
      dispatch({
        type: RoutePlanningActionType.UPDATE_START_LOCATION_FOR_ALL_DRIVERS,
        payload: action.payload
      });
      if (startLocationType === AddressType.CUSTOM || startLocationType === AddressType.WAREHOUSE) {
        const timezone = await getTimeZoneWithCache(startLocation);
        dispatch({
          type: RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_ALL_DRIVERS,
          payload: {startTimeZone: timezone}
        });
      } else {
        const drivers = getState().selectedDrivers;
        const timezones = await Promise.all(
          drivers.map((driver) => getTimeZoneWithCache(getDriverAddressText(driver)))
        );
        timezones.forEach((timezone, index) => {
          dispatch({
            type: RoutePlanningActionType.UPDATE_START_TIME_ZONE_FOR_DRIVER,
            payload: {
              driverId: drivers[index].driverId!,
              manifestDriverId: drivers[index].manifestDriverId ?? undefined,
              startTimeZone: timezone
            }
          });
        });
      }
    },
  [RoutePlanningAsyncActionType.UPDATE_END_LOCATION_FOR_DRIVER_ASYNC]:
    ({dispatch}) =>
    async (action) => {
      dispatch({
        type: RoutePlanningActionType.UPDATE_END_LOCATION_FOR_DRIVER,
        payload: action.payload
      });
    },
  [RoutePlanningAsyncActionType.UPDATE_END_LOCATION_TYPE_FOR_DRIVER_ASYNC]:
    ({dispatch, getState}) =>
    async (action) => {
      const {driverId, manifestDriverId, endLocationType} = action.payload;
      dispatch({
        type: RoutePlanningActionType.UPDATE_END_LOCATION_TYPE_FOR_DRIVER,
        payload: action.payload
      });
      const targetDriver = findDriverByKey(getState().selectedDrivers, driverId, manifestDriverId);
      const endLocation =
        endLocationType === AddressType.HOME
          ? getDriverAddressText(targetDriver!)
          : endLocationType === AddressType.START_LOCATION
          ? targetDriver!.startLocation
          : action.payload.endLocation ?? null;
      dispatch({
        type: RoutePlanningActionType.UPDATE_END_LOCATION_FOR_DRIVER,
        payload: {driverId, manifestDriverId, endLocation}
      });
    },
  [RoutePlanningAsyncActionType.UPDATE_END_LOCATION_FOR_ALL_DRIVERS_ASYNC]:
    ({dispatch}) =>
    async (action) => {
      dispatch({
        type: RoutePlanningActionType.UPDATE_END_LOCATION_FOR_ALL_DRIVERS,
        payload: action.payload
      });
    }
};

export const RoutePlanningContext = createContext(InitialState);

export const RoutePlanningDispatchContext = createContext<
  React.Dispatch<RoutePlanningAction | RoutePlanningAsyncAction> | undefined
>(undefined);

export const RoutePlanningProvider = ({children}: RoutePlanningProviderProps) => {
  const [state, dispatch] = useReducerAsync(reducer, InitialState, asyncActions);

  return (
    <RoutePlanningContext.Provider value={state}>
      <RoutePlanningDispatchContext.Provider value={dispatch}>{children}</RoutePlanningDispatchContext.Provider>
    </RoutePlanningContext.Provider>
  );
};

export const useRoutePlanningContext = () => {
  const context = useContext(RoutePlanningContext);
  if (context === undefined) {
    throw new Error("useRoutePlanningContext must be used within a RoutePlanningProvider");
  }
  return context;
};

export const useRoutePlanningDispatch = () => {
  const context = useContext(RoutePlanningDispatchContext);
  if (context === undefined) {
    throw new Error("useRoutePlanningDispatch must be used within a RoutePlanningProvider");
  }
  return context;
};
